import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import UserDetailsService from '../../../../shared/services/user-details/user-details.service';
import { IEditFileUploadLineProject, IFileUploadLineProject } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';

export const EditUploadLineProjectDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineProject,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineProject) => void,
  isSubmitting: boolean,
  errorMessage: string,
  errorCode: number,
}> = ({
  open,
  closeHandler,
  submitHandler,
  editUploadLine,
  isSubmitting,
  errorMessage,
  errorCode,
}) => {
    //Services
    const userDetailsService = new UserDetailsService()
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [users, setUsers] = useState<any[]>([])
    const [openStartDate, setOpenStartDate] = useState(false)
    const [openEndDate, setOpenEndDate] = useState(false)
    //Formik
    const projectSchema = Yup.object().shape({
      summary: Yup.string().required(`Please give this ${screenLabelsContext.getLabel('project')} a summary`),
      projectReference: Yup.string().nullable(),
      description: Yup.string().nullable(),
      startDate: Yup.string().required('Start date is required'),
      endDate: Yup.string()
        .nullable()
        .test('not-before-start', 'End date cannot be before start date', function (value) {
          if (!value || !this.parent.startDate) return true;
          return moment(value).isSameOrAfter(moment(this.parent.startDate), 'day');
        }),
      assignedUserId: Yup.string().nullable(),
    })

    const formik = useFormik<any>({
      initialValues: {
        summary: editUploadLine.summary,
        projectReference: editUploadLine.projectReference,
        description: editUploadLine.description,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        assignedUserId: editUploadLine.assignedUserId,
      },
      validationSchema: projectSchema,
      onSubmit: (values) => {
        const response = formatTransaction(values, '', true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    })

    useEffect(() => {
      formik.setValues({
        summary: editUploadLine.summary,
        projectReference: editUploadLine.projectReference,
        description: editUploadLine.description,
        startDate: editUploadLine.startDate,
        endDate: editUploadLine.endDate,
        assignedUserId: editUploadLine.assignedUserId,
      })

      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })

      customFieldService.getAllActive(`&entryType=project`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
    }, [editUploadLine])

    useEffect(() => {
      if (customFields && customFields.length && (editUploadLine as any)?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = (editUploadLine as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
          formik.setFieldValue(field.id, matchingValue, false)
        })
      }
    }, [customFields, editUploadLine])


    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }


    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='summary'>{screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Summary</InputLabel>
              <OutlinedInput id='summary' label={`${screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Summary`} name='summary'
                value={formik.values.summary}
                onChange={formik.handleChange}
                error={formik.touched.summary && Boolean(formik.errors.summary)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.summary && formik.errors.summary &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.summary as string}</FormHelperText>
              }
              {(errorCode === 1 || errorCode === 2) && errorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='projectReference'>{screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Reference</InputLabel>
              <OutlinedInput id='projectReference' label={`${screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Reference`} name='projectReference'
                value={formik.values.projectReference}
                onChange={formik.handleChange}
                error={formik.touched.projectReference && Boolean(formik.errors.projectReference)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.projectReference && formik.errors.projectReference &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.projectReference as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='description'>{screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Description</InputLabel>
              <OutlinedInput id='description' label={`${screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} Description`} name='description'
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.description && formik.errors.description &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.description as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='Start Date'
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => formik.setFieldValue('startDate', date?.format('YYYY-MM-DD'))}
                  format='DD/MM/YYYY'
                  sx={{ color: 'text.primary' }}
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='End Date'
                  minDate={formik.values.startDate ? moment(formik.values.startDate) : undefined}
                  value={formik.values.endDate ? moment(formik.values.endDate) : null}
                  onChange={(date) => formik.setFieldValue('endDate', date?.format('YYYY-MM-DD'))}
                  format='DD/MM/YYYY'
                  sx={{ color: 'text.primary' }}
                  open={openEndDate}
                  onOpen={() => setOpenEndDate(true)}
                  onClose={() => setOpenEndDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenEndDate(true),
                      error: formik.touched.endDate && Boolean(formik.errors.endDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.endDate && formik.errors.endDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.endDate as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='assignedUserId'>Assigned User</InputLabel>
              <Select
                id='assignedUserId' label='Assigned User' name='assignedUserId'
                value={formik.values.assignedUserId}
                onChange={formik.handleChange}
                error={formik.touched.assignedUserId && Boolean(formik.errors.assignedUserId)}
                sx={{ color: 'text.primary', }}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.sort((a, b) => a.firstName.localeCompare(b.firstName)).map((user, index) => (
                  <MenuItem key={index} value={user.id}>{user.firstName} {user.surname}</MenuItem>
                ))}
              </Select>
              {formik.touched.assignedUserId && formik.errors.assignedUserId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.assignedUserId as string}</FormHelperText>
              }
            </FormControl>
            {customFields && customFields.length > 0 && customFields.map((customField, index) => {
              return (
                <Stack direction='row' spacing={2} key={index} sx={{ mt: 2 }}>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel htmlFor={customField.id}> {customField.fieldName}</InputLabel>
                    <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                      type={customField.fieldType === 'number' ? 'number' : 'text'}
                      value={formik.values[customField.id]}
                      onChange={formik.handleChange}
                      error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                      sx={{ color: 'text.primary' }}></OutlinedInput>
                  </FormControl>
                  {formik.touched[customField.id] && formik.errors[customField.id] &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors[customField.id] ? <div>{formik.errors[customField.id] as string}</div> : null}
                    </FormHelperText>
                  }
                </Stack>
              )
            })}
          </DialogContent>
          <DialogActions sx={{ pt: 2, pl: 0, pr: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.handleSubmit}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }
